import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { register, spaLogin as apiSpaLogin } from '../api/auth'; 
import { Link, useNavigate } from 'react-router-dom'; 
import styles from './LoginPage.module.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';


const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [agreedDocIds, setAgreedDocIds] = useState({});
  const [viewedDocIds, setViewedDocIds] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loadingRequiredDocs, setLoadingRequiredDocs] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation('register');

  useEffect(() => {
    const fetchRequiredDocs = async () => {
      setLoadingRequiredDocs(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/terms/required`, {
          params: { role: 'buyer' },
        });
        const docs = Array.isArray(res.data?.documents) ? res.data.documents : [];
        setRequiredDocs(docs);
      } catch (error) {
        setFieldErrors((prev) => ({
          ...prev,
          agreements: [t('error.fetchDocsError')],
        }));
      } finally {
        setLoadingRequiredDocs(false);
      }
    };

    fetchRequiredDocs();
  }, []);

  const getFieldError = (name) => fieldErrors?.[name]?.[0] || '';

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev?.[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const buildRegisterError = (err) => {
    const data = err?.response?.data;
    const normalizedFieldErrors = {};

    if (data?.errors && typeof data.errors === 'object') {
      Object.entries(data.errors).forEach(([key, value]) => {
        const messages = Array.isArray(value) ? value.map((msg) => String(msg)) : [String(value)];
        normalizedFieldErrors[key] = messages;
      });
    }

    if (data?.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors).flat().map((msg) => String(msg));
      if (messages.length > 0) {
        return {
          message: Array.from(new Set(messages)).join(' / '),
          fieldErrors: normalizedFieldErrors,
        };
      }
    }

    if (data?.message) {
      return {
        message: String(data.message),
        fieldErrors: normalizedFieldErrors,
      };
    }

    return {
      message: t('error.registerFailed'),
      fieldErrors: normalizedFieldErrors,
    };
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleRegister();
  };

  const handleRegister = async () => {
    setFieldErrors({});

    if (loadingRequiredDocs) {
      setFieldErrors({ agreements: [t('error.loadingDocs')] });
      return;
    }

    if (requiredDocs.length === 0) {
      setFieldErrors({ agreements: [t('error.fetchDocsFailed')] });
      return;
    }

    if (password !== passwordConfirmation) {
      setFieldErrors({ password_confirmation: [t('error.passwordMismatch')] });
      return;
    }

    const missingDoc = requiredDocs.find((doc) => !agreedDocIds[doc.id]);
    if (missingDoc) {
      setFieldErrors({ agreements: [t('error.agreementsRequired')] });
      return;
    }

    const agreements = requiredDocs.map((doc) => ({ terms_version_id: doc.id }));

    try {
      await register({
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
        role: 'buyer',
        agreements,
      });

      let resendVerificationToken = '';

      try {
        await apiSpaLogin({ email, password });
      } catch (loginError) {
        const responseData = loginError?.response?.data;
        if (responseData?.error_code === 'email_unverified') {
          resendVerificationToken = responseData?.resend_verification_token || '';
        }
      }

      navigate('/email-verification-notice', {
        state: {
          email,
          resendVerificationToken,
        },
      });
    } catch (err) {
      console.error('登録失敗:', err);
      const parsed = buildRegisterError(err);
      if (Object.keys(parsed.fieldErrors).length > 0) {
        setFieldErrors(parsed.fieldErrors);
      } else {
        setFieldErrors({ email: [parsed.message] });
      }
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('title')}</h2>

      <form onSubmit={handleSubmit}>
        <label htmlFor="register-name" style={{ display: 'block', marginBottom: 4 }}>{t('nameLabel')}</label>
        <input
          id="register-name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearFieldError('name');
          }}
          placeholder={t('namePlaceholder')}
          autoComplete="name"
          className={getFieldError('name') ? styles.inputError : ''}
          style={{ display: 'block', marginBottom: '10px' }}
        />
        {getFieldError('name') && <p className={styles.fieldError}>{getFieldError('name')}</p>}
        <label htmlFor="register-email" style={{ display: 'block', marginBottom: 4 }}>{t('emailLabel')}</label>
        <input
          id="register-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError('email');
          }}
          placeholder="email@example.com"
          autoComplete="email"
          className={getFieldError('email') ? styles.inputError : ''}
          style={{ display: 'block', marginBottom: '10px' }}
        />
        {getFieldError('email') && <p className={styles.fieldError}>{getFieldError('email')}</p>}
        <label htmlFor="register-password" style={{ display: 'block', marginBottom: 4 }}>{t('passwordLabel')}</label>
        <input
          id="register-password"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearFieldError('password');
          }}
          placeholder={t('passwordPlaceholder')}
          autoComplete="new-password"
          className={getFieldError('password') ? styles.inputError : ''}
          style={{ display: 'block', marginBottom: '10px' }}
        />
        {getFieldError('password') && <p className={styles.fieldError}>{getFieldError('password')}</p>}
        <input
          id="register-password-confirmation"
          type="password"
          value={passwordConfirmation}
          onChange={(e) => {
            setPasswordConfirmation(e.target.value);
            clearFieldError('password_confirmation');
          }}
          placeholder={t('passwordConfirmPlaceholder')}
          autoComplete="new-password"
          className={getFieldError('password_confirmation') ? styles.inputError : ''}
          style={{ display: 'block', marginBottom: '10px' }}
        />
        {getFieldError('password_confirmation') && <p className={styles.fieldError}>{getFieldError('password_confirmation')}</p>}

        <div style={{ marginTop: 10, marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('agreements')}</div>
          {loadingRequiredDocs && <p style={{ margin: '0 0 8px', color: '#555' }}>{t('agreementsLoading')}</p>}
          {requiredDocs.map((doc) => (
            <label key={doc.id} style={{ display: 'block', marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={!!agreedDocIds[doc.id]}
                disabled={!viewedDocIds[doc.id]}
                onChange={(e) => {
                  setAgreedDocIds((prev) => ({ ...prev, [doc.id]: e.target.checked }));
                  clearFieldError('agreements');
                }}
              />{' '}
              <button
                type="button"
                onClick={() => {
                  setViewedDocIds((prev) => ({ ...prev, [doc.id]: true }));
                  setPreviewDoc(doc);
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'blue',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                }}
              >
              {t('termsTitle.' + doc.type, { defaultValue: doc.title })}{t('agreeTo')}
              </button>
            </label>
          ))}
          {requiredDocs.length > 0 && (
            <p style={{ margin: '4px 0 0', color: '#555', fontSize: 12 }}>{t('agreementsHint')}</p>
          )}
          {getFieldError('agreements') && <p className={styles.fieldError}>{getFieldError('agreements')}</p>}
        </div>

        <button className={styles.loginButton} type="submit" disabled={loadingRequiredDocs}>{t('submit')}</button>
      </form>
      <div className={styles.linkRow}>
        <Link to="/seller-register" style={{ color: 'blue', textDecoration: 'underline' }}>
          {t('sellerRegister')}
        </Link>
      </div>

      {previewDoc && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1000,
          }}
          onClick={() => setPreviewDoc(null)}
        >
          <div
            style={{
              width: 'min(840px, 100%)',
              maxHeight: '80vh',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 12,
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px' }}>{t('termsTitle.' + previewDoc.type, { defaultValue: previewDoc.title })}</h3>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewDoc.content || '') }} />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <button type="button" onClick={() => setPreviewDoc(null)}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
